import { toArray } from 'lodash-es';
import { useMemo } from 'react';
import type {
  UseSystemPackageVersionSummaryStepParams,
  UseSystemPackageVersionSummaryStepReturn,
} from './systemPackageVersionSummaryStep.model';
import {
  buildDeviceAppVersionFilesListItemFromTemplates,
  convertExistingTemplatesToFilesListItem,
} from './systemPackageVersionSummaryStep.util';

export function useSystemPackageVersionSummaryStep({
  data,
  applicationFiles,
  existingTemplates,
  t,
}: UseSystemPackageVersionSummaryStepParams): UseSystemPackageVersionSummaryStepReturn {
  const applicationFilesList = useMemo(() => {
    return buildDeviceAppVersionFilesListItemFromTemplates({
      templates: data?.build?.applicationFiles,
      targetLocationFieldLabel: t('input.parameterTemplates.label'),
      versionFieldLabel: t('input.version.label'),
      nameFieldLabel: t('field.name'),
      idFieldLabel: t('field.id'),
      idFieldSize: t('field.size'),
      t,
    });
  }, [data?.build?.applicationFiles, t]);

  const existingAppFilesList = useMemo(
    () =>
      convertExistingTemplatesToFilesListItem({
        existingTemplatesFormValues: toArray(data?.build?.existingAppFiles),
        existingTemplates: toArray(applicationFiles),
        targetLocationFieldLabel: t('input.parameterTemplates.label'),
        versionFieldLabel: t('input.version.label'),
        nameFieldLabel: t('field.name'),
        idFieldLabel: t('field.id'),
        idFieldSize: t('field.size'),
        t,
      }),
    [applicationFiles, data?.build?.existingAppFiles, t],
  );

  const parameterTemplatesList = useMemo(
    () =>
      buildDeviceAppVersionFilesListItemFromTemplates({
        templates: data?.configure?.parameterTemplates,
        targetLocationFieldLabel: t('input.parameterTemplates.label'),
        versionFieldLabel: t('input.version.label'),
        nameFieldLabel: t('field.name'),
        idFieldLabel: t('field.id'),
        idFieldSize: t('field.size'),
        t,
      }),
    [data?.configure?.parameterTemplates, t],
  );
  const existingTemplatesList = useMemo(
    () =>
      convertExistingTemplatesToFilesListItem({
        existingTemplatesFormValues: toArray(data?.configure?.existingTemplates),
        existingTemplates: toArray(existingTemplates),
        targetLocationFieldLabel: t('input.parameterTemplates.label'),
        versionFieldLabel: t('input.version.label'),
        nameFieldLabel: t('field.name'),
        idFieldLabel: t('field.id'),
        idFieldSize: t('field.size'),
        t,
      }),
    [data?.configure?.existingTemplates, existingTemplates, t],
  );

  const shouldDisplayConfigurationCard = useMemo(
    () => !!data?.configure?.existingTemplates?.length || parameterTemplatesList.length > 0,
    [data, parameterTemplatesList],
  );

  return useMemo(
    () => ({
      shouldDisplayConfigurationCard,
      parameterTemplatesList,
      existingAppFilesList,
      existingTemplatesList,
      applicationFilesList,
      t,
    }),
    [
      shouldDisplayConfigurationCard,
      parameterTemplatesList,
      existingAppFilesList,
      existingTemplatesList,
      applicationFilesList,
      t,
    ],
  );
}
